import React, {useEffect, useState} from 'react';
import {InlineAlert} from '@/components/alerts';
import CellLoader from '@/components/loading/CellLoader';
import useData from '@/utils/useData';
import {EmailEvent} from 'entities/event';
import {buildURL} from '@/utils/urlBuilder';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';

interface Props {
    eventData: EmailEvent;
}

const EmailDetailBlock = React.memo<Props>(({eventData}) => {
    const {config, loading: configLoading} = useOptimizedConfig();

    // Support both `id` and `event_id` depending on where the component is used
    const eventId = (eventData as any)?.event_id ?? (eventData as any)?.id;

    const emailUrl = config && eventId
        ? buildURL('getEmailConversation', {event_id: eventId})
        : null;

    const [activeEmailData, loadingEmail, , emailError] = useData<any>(
        emailUrl, 'eventDetail', 'setActiveEmailData', 'activeEmailData',
    );

    // Avoid showing a "flash" of the loader for very fast responses
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        let timer: number | undefined;
        if (loadingEmail) {
            timer = window.setTimeout(() => setShowLoader(true), 200);
        } else {
            setShowLoader(false);
        }

        return () => {
            if (timer) {
                window.clearTimeout(timer);
            }
        };
    }, [loadingEmail]);

    // Show loading while config is loading
    if (configLoading || !config) {
        return <CellLoader text={i18n.t('loading_configuration.text')} />;
    }

    const hasEmailEndpoint = !!emailUrl;
    const isEmailLoading = hasEmailEndpoint ? loadingEmail : false;

    let scrollDiv;

    function createBody() {
        const body: string | undefined = activeEmailData?.body;
        if (!body || typeof body !== 'string') {
            return {__html: ''};
        }
        // Use a RegExp replace for wider TS/browser compatibility instead of String.prototype.replaceAll
        const sanitizedBody = body.replace(/<style/gi, '<p style="display:none;"');
        return {__html: sanitizedBody};
    }

    if (isEmailLoading && showLoader) {
        scrollDiv = (
            <CellLoader fullHeight={true} />
        );
    } else if (!hasEmailEndpoint) {
        // We don't have a valid event ID to build the email URL – show a graceful fallback
        scrollDiv = (
            <InlineAlert
                variant="warning"
                message={i18n.t('jt_email_not.text')}
                className="mb-3"
            />
        );
    } else if (emailError) {
        scrollDiv = (
            <InlineAlert
                variant="error"
                message={i18n.t('jt_email_not.text')}
                className="mb-3"
            />
        );
    } else if (activeEmailData && (activeEmailData as any).length === 0) {
        scrollDiv = (
            <InlineAlert
                variant="warning"
                message={i18n.t('jt_email_not.text')}
                className="mb-3"
            />
        );
    } else {
        scrollDiv = (
            <div>
                {activeEmailData?.subject && (
                    <h5 className="mb-3 weight-300">{i18n.t('subject.text')} {activeEmailData?.subject}</h5>
                )}
                <div dangerouslySetInnerHTML={createBody()} />
                {activeEmailData?.attachmentExists && (
                    <div>
                        <hr className="my-2 custom-separator" />
                        <h6 className="weight-300 my-3" style={{color: '#54667a'}}>
                            <Icon name="mdi-paperclip" className="mr-2" style={{fontSize: '0.7rem'}} />
                            <span>{i18n.t('jt_attachment_exists.text')}</span>
                        </h6>
                    </div>
                )}
            </div>
        )
            ;
    }

    return (
        <div>
            {scrollDiv}
        </div>
    );
});


export default EmailDetailBlock;
